import React, { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import './EventWrapper.css';

interface EventWrapperProps {
  title: string;
  description: string;
  planDuration?: string;
  planIncludes?: string;
  children: ReactNode;
  loading?: boolean;
  error?: string | null;
  eventsCount?: number;
  seeMoreLink?: string;
  seeMoreText?: string;
  showPlan?: boolean;
  loadingSkeletonCount?: number;
}

const EventWrapper: React.FC<EventWrapperProps> = ({
  title,
  description,
  planDuration = "1 month",
  planIncludes,
  children,
  loading = false,
  error = null,
  eventsCount = 0,
  seeMoreLink = "#",
  seeMoreText = "See More",
  showPlan = true,
  loadingSkeletonCount = 3
}) => {
  
  if (loading) {
    return (
      <div className="w-full relative">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: loadingSkeletonCount }).map((_, i) => (
            <div key={i} className="bg-gray-800 rounded-2xl h-96 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full relative">
        <div className="text-red-500 text-center py-8">
          Error loading {title.toLowerCase()} events: {error}
        </div>
      </div>
    );
  }

  if (eventsCount === 0) {
    return (
      <div className="w-full relative">
        <div className="text-white text-center py-8">
          No {title.toLowerCase()} events found
        </div>
      </div>
    );
  }

  return (
    <div className="w-full relative">
      {/* Header with Title and Plan Info */}
      <div className="flex justify-start items-start mb-4">
        <div className="justify-start text-white text-2xl font-bold">
          {title}
        </div>
        
        {showPlan && planIncludes && (
          <div className="bg-gray-100 rounded-sm outline outline-1 outline-offset-[-1.10px] outline-black/0 inline-flex justify-center items-center gap-1 overflow-hidden ms-8 mt-1 px-1 py-0">
            <div className="justify-start event-wrapper-plan-content">
              <span className="text-orange-600 text-sm font-bold underline leading-4">
                Your Plan ({planDuration})
              </span>
              <span className="text-gray-950 text-sm font-medium leading-4 ms-1">
                include : {planIncludes}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      <div className="w-full justify-start text-white text-lg font-normal mb-8">
        {description}
      </div>

      {/* Children (Event Cards) */}
      {children}

      {/* See More Button */}
      <div className="flex justify-center">
        <Link 
          to={seeMoreLink} 
          className="bg-orange-500 rounded-full outline outline-[1.70px] outline-offset-[-1.70px] outline-slate-800 inline-flex justify-start items-center gap-4 overflow-hidden px-16 py-3.5 hover:bg-orange-600 transition-colors"
        >
          <div className="text-center text-white text-lg font-normal">
            {seeMoreText}
          </div>
          <div className="w-6 h-6 relative text-black">
            <ExternalLink size={22} color="currentColor" strokeWidth={2}/>            
          </div>
        </Link>
      </div>
    </div>
  );
};

export default EventWrapper;